import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import './Home.css';

const HERO_IMG = '/firestruck.jpg';
const PORTRAIT_IMG = '/homepage.jpg';
const YARD_SIGN = '/favicon.png';

function useAnimateOnScroll() {
  const ref = useRef();
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const els = ref.current?.querySelectorAll('.animate');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
}

function Home() {
  const pageRef = useAnimateOnScroll();

  return (
    <div className="home" ref={pageRef}>
      {/* HERO */}
      <section className="hero">
        <div className="hero__content">
          <span className="hero__label animate">Mayor of Horseheads</span>
          <h1 className="hero__title animate">
            Accelerating<br />Our Community
          </h1>
          <p className="hero__desc animate">
            Here to be your voice by listening to your concerns.
            Building a future where the next generation wants to stay.
          </p>
          <div className="hero__actions animate">
            <Link to="/vision" className="btn btn--primary">See the Vision</Link>
            <Link to="/contact" className="btn btn--ghost">Get in Touch</Link>
          </div>
        </div>
        <div className="hero__image animate">
          <img src={HERO_IMG} alt="Kevin Adams at community event" />
        </div>
      </section>

      {/* STATS */}
      <section className="stats">
        <div className="stats__grid">
          <div className="stats__item animate">
            <span className="stats__number">15+</span>
            <span className="stats__label">Years Public Service</span>
          </div>
          <div className="stats__item animate">
            <span className="stats__number">3</span>
            <span className="stats__label">Generations in Horseheads</span>
          </div>
          <div className="stats__item animate">
            <span className="stats__number">13</span>
            <span className="stats__label">Years at Youth Bureau</span>
          </div>
          <div className="stats__item animate">
            <span className="stats__number">100%</span>
            <span className="stats__label">Community Dedicated</span>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="intro">
        <div className="intro__inner">
          <div className="intro__image animate">
            <img src={PORTRAIT_IMG} alt="Kevin Adams portrait" />
          </div>
          <div className="intro__text">
            <span className="label animate">About Kevin</span>
            <h2 className="animate">Born &amp; raised in Horseheads</h2>
            <p className="animate">
              Three generations of the Adams family have called Horseheads home —
              drawn by safety, a strong sense of community, and an excellent
              school system. Kevin lives in the Village with his daughter, Lily,
              and his wife, Whitney.
            </p>
            <p className="animate">
              With nearly 15 years of public service, a Master's in Business,
              and a Bachelor's in Marketing from RIT, Kevin brings both heart
              and expertise to village leadership.
            </p>
            <Link to="/qualifications" className="link animate">
              Full background
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* PRIORITIES PREVIEW */}
      <section className="priorities">
        <div className="priorities__header">
          <span className="label animate">Platform</span>
          <h2 className="animate">Six priorities for Horseheads</h2>
        </div>
        <div className="priorities__grid">
          {[
            { title: 'Government Efficiency', desc: 'Strategic planning, performance metrics, and a village report card.' },
            { title: 'Stronger Partnerships', desc: 'County coordination and shared services with the Town.' },
            { title: 'Community Events', desc: 'More gatherings at Hanover Square with local partnerships.' },
            { title: 'Real Accountability', desc: 'Regular public forums and transparent leadership.' },
            { title: 'Infrastructure First', desc: 'Water system rebuild, audits, and park investment.' },
            { title: 'Digital Government', desc: 'A .gov website, social media, and modern operations.' },
          ].map((item, i) => (
            <div className="priorities__card animate" key={i}>
              <span className="priorities__num">{String(i + 1).padStart(2, '0')}</span>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
        <div className="priorities__cta animate">
          <Link to="/vision" className="link">
            Read the full platform
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </Link>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="cta-banner">
        <div className="cta-banner__inner animate">
          <img src={YARD_SIGN} alt="Adams for Mayor yard sign" className="cta-banner__badge" />
          <div>
            <h2>Ready to accelerate Horseheads forward?</h2>
            <p>Your voice matters. Share your concerns and ideas.</p>
          </div>
          <Link to="/contact" className="btn btn--dark">Let's Talk</Link>
        </div>
      </section>
    </div>
  );
}

export default Home;
